import type { ProductCategory } from '../../../types/inventory';

export const CATEGORY_IMPORT_EXPORT_HEADERS = ['Nom'] as const;

export type CategoryImportDraftRow = {
  rowIndex: number;
  name: string;
};

function norm(s: string): string {
  return s.trim().toLowerCase();
}

function mapCategoryHeader(cell: unknown): boolean {
  const h = String(cell ?? '').trim();
  const n = norm(h);
  return n === 'nom' || n === 'name' || n === 'categorie' || n === 'catégorie' || n === 'category';
}

/**
 * Première colonne reconnue comme « Nom », ou une seule colonne de données sans en-tête explicite.
 */
export function parseCategoryImportMatrix(rows: unknown[][]): CategoryImportDraftRow[] {
  if (!rows.length) return [];

  let headerRowIndex = -1;
  const colIndex = 0;

  for (let r = 0; r < Math.min(rows.length, 20); r++) {
    const line = rows[r] || [];
    if (line.length >= 1 && mapCategoryHeader(line[0])) {
      headerRowIndex = r;
      break;
    }
  }

  const dataStart = headerRowIndex >= 0 ? headerRowIndex + 1 : 0;
  const out: CategoryImportDraftRow[] = [];
  let rowNum = dataStart + 1;

  for (let r = dataStart; r < rows.length; r++) {
    const line = rows[r] || [];
    const raw = line[colIndex] ?? line[0];
    const name = String(raw ?? '').trim();
    if (!name && line.every((c) => String(c ?? '').trim() === '')) {
      rowNum += 1;
      continue;
    }
    out.push({ rowIndex: rowNum, name });
    rowNum += 1;
  }

  return out;
}

export type CategoryRowValidationIssue = { rowIndex: number; messages: string[] };

export function validateCategoryImportDrafts(
  drafts: CategoryImportDraftRow[],
  existingCategories: ProductCategory[]
): CategoryRowValidationIssue[] {
  const issues: CategoryRowValidationIssue[] = [];
  const existingLower = new Set(existingCategories.map((c) => norm(c.name)));
  const seenInFile = new Map<string, number>();

  for (const d of drafts) {
    const messages: string[] = [];
    const n = d.name.trim();
    if (!n) {
      messages.push('Le nom est obligatoire.');
    } else {
      if (n.length > 120) {
        messages.push('Nom trop long (120 caractères max).');
      }
      const nl = norm(n);
      if (existingLower.has(nl)) {
        messages.push('Une catégorie avec ce nom existe déjà dans le business.');
      }
      if (seenInFile.has(nl)) {
        messages.push(`Doublon avec la ligne ${seenInFile.get(nl)}.`);
      } else {
        seenInFile.set(nl, d.rowIndex);
      }
    }
    if (messages.length) {
      issues.push({ rowIndex: d.rowIndex, messages });
    }
  }

  return issues.sort((a, b) => a.rowIndex - b.rowIndex);
}

export function categoryIssuesByRowIndex(issues: CategoryRowValidationIssue[]): Map<number, string[]> {
  const m = new Map<number, string[]>();
  for (const i of issues) {
    m.set(i.rowIndex, i.messages);
  }
  return m;
}

export function categoryImportHasBlockingIssues(issues: CategoryRowValidationIssue[]): boolean {
  return issues.length > 0;
}
